import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { CloseIcon, CollapseIcon, ExpandIcon, SaveIcon } from '@platform/ui-kit';
import { api, type AprApplication, type AprRunRate } from '../../api';
import { describeError } from '../admin/useAsyncData';
import { formatCurrency, formatAccounting, parseAmount } from './shared';

/**
 * The Current Run Rate detail panel, measured from APR.
 *
 * Deliberately NOT the same shape as the Application Master Data panel — the two
 * differ in the source app and reproducing one from the other is how they drift:
 *
 *   width      640px (38%) with a rgba(0,0,0,0.25) backdrop — this one DOES dim the
 *              page behind it, where the Applications panel does not
 *   layout     ONE scrolling column of cards, not two panes
 *   header     "View Cost Record" / "Edit Cost Record" — no chip, no record code,
 *              and no Back button
 *   controls   expand + close; save appears only while editing
 *
 * Three sections: Application Information, Cost Information, TCO Status. There is no
 * attachment area and nothing interactive below the fold; save and close live in the
 * fixed header.
 */

const PANEL_WIDTH = 640;

/** APR's field label: 16px/500 #333, 6px below. Larger than the Applications panel. */
const label = (text: string) => (
  <Typography
    component="label"
    sx={{ display: 'block', fontSize: 16, fontWeight: 500, color: '#333333', mb: '6px' }}
  >
    {text}
  </Typography>
);

// Both kinds of control are pinned to the same 36px height, so a text field and a
// select side by side line up — the select's larger type otherwise made it ~2px taller.
const FIELD_SX = {
  '& .MuiOutlinedInput-root': { borderRadius: '4px', bgcolor: 'background.paper', height: 36 },
  '& .MuiOutlinedInput-input': { padding: '8px 12px', fontSize: 13 },
  '& .MuiOutlinedInput-notchedOutline': { borderWidth: '1.25px', borderColor: '#e0e0e0' },
} as const;

/** Selects run a shade larger than text inputs in APR — 14px, #ddd. */
const SELECT_SX = {
  '& .MuiOutlinedInput-root': { borderRadius: '4px', bgcolor: 'background.paper', height: 36 },
  '& .MuiOutlinedInput-input': { padding: '8px 12px', fontSize: 14 },
  '& .MuiOutlinedInput-notchedOutline': { borderWidth: '1.25px', borderColor: '#dddddd' },
} as const;

const CONTROL_BASE = {
  width: 36,
  height: 36,
  borderRadius: '9px',
  border: '1.25px solid',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  font: 'inherit',
} as const;

const ICON_BUTTON_SX = {
  ...CONTROL_BASE,
  borderColor: '#cbd5e1',
  bgcolor: '#f1f5f9',
  color: '#475569',
} as const;

const SAVE_BUTTON_SX = {
  ...CONTROL_BASE,
  borderColor: '#16a34a',
  bgcolor: '#dcfce7',
  color: '#15803d',
} as const;

const CLOSE_BUTTON_SX = {
  ...CONTROL_BASE,
  borderColor: '#dc2626',
  bgcolor: '#fee2e2',
  color: '#b91c1c',
} as const;

/** A titled card. APR groups the panel's fields into these. */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: '8px',
        border: '1.25px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Typography sx={{ fontSize: 17.6, fontWeight: 600, color: '#495057', mb: 2 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

/** Two equal columns with a 16px gutter, as APR lays the fields out. */
function FieldGrid({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        gap: '16px',
        // A label that wraps to two lines ("Total Annual Cost to Operate and Support")
        // pushed its input below the one beside it. Each cell now stacks label + input
        // at the BOTTOM of the row, so the inputs in a row always share a baseline.
        '& > *': { display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' },
      }}
    >
      {children}
    </Box>
  );
}

const COST_FIELDS = [
  { key: 'total_annual_cost', label: 'Total Annual Cost to Operate and Support' },
  { key: 'annual_software_licensing_fees', label: 'Annual Software Licensing Fees' },
  { key: 'annual_third_party_vendor_support', label: 'Annual 3rd Party Vendor Support Cost' },
  { key: 'annual_infrastructure_cost', label: 'Annual Infrastructure Cost' },
  { key: 'indirect_operational_costs', label: 'Indirect Operational Costs' },
  { key: 'cloud_saas_subscription_cost_impact', label: 'Cloud/SaaS Subscription Costs' },
] as const;

const TCO_STATUSES = ['Not Defined', 'Defined'] as const;

/** APR's status badges: green when defined, red when not. */
const TCO_BADGE = {
  Defined: { color: '#006100', bgcolor: '#c6efce' },
  'Not Defined': { color: '#9c0006', bgcolor: '#ffc7ce' },
} as const;

type Draft = Record<string, string>;

function toDraft(cost: AprRunRate): Draft {
  return {
    application_id: cost.application_id,
    application_name: cost.application_name,
    tco_status: cost.tco_status,
    // Accounting from the moment it loads, so a field never shows one format before
    // it has been touched and another after.
    ...Object.fromEntries(COST_FIELDS.map((f) => [f.key, formatAccounting(Number(cost[f.key] ?? 0))])),
  };
}

export interface RunRateDetailDrawerProps {
  cost: AprRunRate | null;
  mode: 'view' | 'edit';
  onModeChange: (mode: 'view' | 'edit') => void;
  onClose: () => void;
  onSaved: () => void;
  /** Every application, for the Application selector. */
  applications?: readonly AprApplication[];
}

export function RunRateDetailDrawer({
  cost,
  mode,
  onModeChange,
  onClose,
  onSaved,
  applications = [],
}: RunRateDetailDrawerProps) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [maximized, setMaximized] = useState(false);

  useEffect(() => {
    setDraft(cost ? toDraft(cost) : null);
    setError(undefined);
    // Keyed on identity, not contents — a background reload must not discard an edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cost?.id, mode]);

  const editing = mode === 'edit';
  const set = (key: string, value: string) =>
    setDraft((current) => (current ? { ...current, [key]: value } : current));

  const save = async () => {
    if (!cost || !draft) return;
    setSaving(true);
    setError(undefined);
    try {
      await api.apr.updateRunRate(cost.id, {
        application_name: draft.application_name,
        tco_status: draft.tco_status,
        ...Object.fromEntries(COST_FIELDS.map((f) => [f.key, parseAmount(draft[f.key])])),
      });
      onSaved();
      onModeChange('view');
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSaving(false);
    }
  };

  const badge = TCO_BADGE[(draft?.tco_status as keyof typeof TCO_BADGE) ?? 'Not Defined'];

  /**
   * Live reconciliation: the five components re-sum as they are typed, and the total
   * is flagged when it drifts more than a penny from them.
   *
   * Recalculate SETS the total to the sum rather than adjusting a component — the
   * components are separately sourced and the total is the derived figure.
   */
  const componentTotal = draft
    ? COST_FIELDS.filter((f) => f.key !== 'total_annual_cost').reduce(
        (sum, f) => sum + parseAmount(draft[f.key]),
        0,
      )
    : 0;
  const recordedTotal = parseAmount(draft?.total_annual_cost);
  const mismatch = componentTotal > 0 && Math.abs(recordedTotal - componentTotal) > 0.01;

  return (
    <Drawer
      anchor="right"
      open={Boolean(cost)}
      onClose={onClose}
      // This panel DOES dim the page behind it — 25% black, APR's value. Clicking the
      // backdrop closes it, which is one of only two ways out.
      slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.25)' } } }}
      // Above the app bar, or its controls sit underneath and there is no way out.
      sx={{ zIndex: (t) => t.zIndex.modal + 2 }}
      PaperProps={{
        sx: {
          width: { xs: '100%', md: maximized ? '98vw' : PANEL_WIDTH },
          maxWidth: '100%',
          boxShadow: 'rgba(0, 0, 0, 0.15) -4px 0px 24px 0px',
        },
      }}
    >
      {cost && draft ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              px: '18px',
              py: '14px',
              gap: 2,
              borderBottom: '1px solid',
              borderColor: '#e5e5e5',
              flexShrink: 0,
            }}
          >
            <Typography sx={{ fontSize: 17, fontWeight: 600, color: '#1a1a1a' }} noWrap>
              {editing ? 'Edit Cost Record' : 'View Cost Record'}
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center">
              {editing ? (
                <Tooltip title="Save changes">
                  <Box
                    component="button"
                    type="button"
                    onClick={() => void save()}
                    disabled={saving}
                    sx={SAVE_BUTTON_SX}
                  >
                    <SaveIcon sx={{ fontSize: 20 }} />
                  </Box>
                </Tooltip>
              ) : null}
              <Tooltip title={maximized ? 'Restore' : 'Maximize'}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => setMaximized((m) => !m)}
                  sx={ICON_BUTTON_SX}
                >
                  {maximized ? (
                    <CollapseIcon sx={{ fontSize: 19 }} />
                  ) : (
                    <ExpandIcon sx={{ fontSize: 19 }} />
                  )}
                </Box>
              </Tooltip>
              <Tooltip title="Close">
                <Box component="button" type="button" onClick={onClose} sx={CLOSE_BUTTON_SX}>
                  <CloseIcon sx={{ fontSize: 20 }} />
                </Box>
              </Tooltip>
            </Stack>
          </Stack>

          {saving ? <LinearProgress /> : null}

          <Stack spacing={2} sx={{ flex: 1, overflowY: 'auto', px: '18px', py: 2 }}>
            {error ? <Alert severity="error">{error}</Alert> : null}

            <Section title="Application Information">
              <FieldGrid>
                <Box>
                  {label('Application *')}
                  <TextField
                    select
                    fullWidth
                    disabled={!editing}
                    value={draft.application_id}
                    onChange={(e) => {
                      const app = applications.find((a) => a.id === e.target.value);
                      set('application_id', e.target.value);
                      if (app) set('application_name', app.name);
                    }}
                    sx={SELECT_SX}
                  >
                    <MenuItem value="">Select an application...</MenuItem>
                    {applications.map((app) => (
                      <MenuItem key={app.id} value={app.id} sx={{ fontSize: 14 }}>
                        {app.application_id_code} — {app.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Box>
                  {label('Application Name *')}
                  <TextField
                    fullWidth
                    disabled={!editing}
                    value={draft.application_name}
                    onChange={(e) => set('application_name', e.target.value)}
                    sx={FIELD_SX}
                  />
                </Box>
              </FieldGrid>
            </Section>

            <Section title="Cost Information">
              {mismatch ? (
                <Alert
                  severity="warning"
                  sx={{ mb: 2, fontSize: 13 }}
                  action={
                    editing ? (
                      <Box
                        component="button"
                        type="button"
                        // Written in accounting form, like every other amount here.
                        // `String(componentTotal)` put a raw "4231381.02" into a column
                        // of grouped figures — the one field the user did not type is
                        // the one that would have been formatted differently.
                        onClick={() => set('total_annual_cost', formatAccounting(componentTotal))}
                        sx={{
                          font: 'inherit',
                          fontWeight: 600,
                          cursor: 'pointer',
                          border: '1.25px solid',
                          borderColor: 'warning.main',
                          borderRadius: '4px',
                          bgcolor: 'transparent',
                          color: 'warning.dark',
                          px: 1.5,
                          py: 0.5,
                        }}
                      >
                        Recalculate
                      </Box>
                    ) : null
                  }
                >
                  Total is {formatCurrency(recordedTotal)}, but the components add up to{' '}
                  {formatCurrency(componentTotal)}.
                </Alert>
              ) : null}
              <FieldGrid>
                {COST_FIELDS.map((field) => (
                  <Box key={field.key}>
                    {label(field.label)}
                    <TextField
                      fullWidth
                      disabled={!editing}
                      // View keeps the currency symbol; edit shows accounting format —
                      // grouped thousands and two decimals, no symbol to type around.
                      value={
                        editing
                          ? draft[field.key]
                          : formatCurrency(parseAmount(draft[field.key]))
                      }
                      // Typing is left completely alone. Reformatting on every keystroke
                      // fights the caret — inserting a separator mid-number jumps the
                      // cursor, and a half-typed "1,2" is not yet a number anyway.
                      onChange={(e) => set(field.key, e.target.value)}
                      // On focus, strip the grouping so the number can be selected and
                      // retyped as one token; on blur, put it back in accounting form.
                      onFocus={(e) => {
                        if (!editing) return;
                        const n = parseAmount(e.target.value);
                        set(field.key, n === 0 ? '' : String(n));
                      }}
                      onBlur={() => set(field.key, formatAccounting(parseAmount(draft[field.key])))}
                      inputProps={editing ? { inputMode: 'decimal' } : {}}
                      sx={{
                        ...FIELD_SX,
                        ...(field.key === 'total_annual_cost' && mismatch
                          ? {
                              '& .MuiOutlinedInput-input': {
                                padding: '8px 12px',
                                fontSize: 13,
                                color: '#dc2626',
                                fontWeight: 600,
                              },
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderWidth: '1.25px',
                                borderColor: '#dc2626',
                              },
                            }
                          : {}),
                      }}
                    />
                  </Box>
                ))}
              </FieldGrid>
            </Section>

            <Section title="TCO Status">
              {editing ? (
                // In the same two-column grid as the sections above, so it is exactly
                // one column wide and lines up with the fields over it.
                <FieldGrid>
                  <Box>
                    <TextField
                      select
                      fullWidth
                      value={draft.tco_status}
                      onChange={(e) => set('tco_status', e.target.value)}
                      sx={SELECT_SX}
                    >
                      {TCO_STATUSES.map((status) => (
                        <MenuItem key={status} value={status} sx={{ fontSize: 14 }}>
                          {status === 'Defined' ? 'Defined — TCO assigned' : status}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </FieldGrid>
              ) : (
                <Box
                  sx={{
                    display: 'inline-block',
                    px: '6px',
                    py: '2px',
                    fontSize: 13,
                    fontWeight: 600,
                    borderRadius: '12px',
                    color: badge.color,
                    bgcolor: badge.bgcolor,
                  }}
                >
                  {draft.tco_status}
                </Box>
              )}
            </Section>
          </Stack>
        </Box>
      ) : null}
    </Drawer>
  );
}
